import React, { FC } from 'react';
import styled from 'styled-components';
import { SkeletonBlock, SkeletonPageHeader, SkeletonPageShell, SkeletonRow, SkeletonStack } from 'src/components/widgets/skeleton';

const MarketplaceOrderSkeletonShell = styled(SkeletonPageShell).attrs({ $padding: '20px' })`
    min-height: 100%;
    background: #f3f5f6;
`;

const MarketplaceOrderHeaderSkeleton = styled(SkeletonPageHeader).attrs({ $margin: '0 0 20px' })`
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
`;

const MarketplaceOrderPanelSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    padding: 20px;
    min-width: 0;
`;

const MarketplaceOrderToolbarSkeleton = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
    min-width: 0;

    @media (max-width: 700px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

const MarketplaceOrderTableSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    border-radius: 8px;
    overflow: hidden;
    min-width: 0;
`;

const MarketplaceOrderTableRowSkeleton = styled.div`
    display: grid;
    grid-template-columns: 70px minmax(180px, 1fr) 130px 90px;
    gap: 18px;
    align-items: center;
    min-height: 64px;
    padding: 14px 18px;
    border-bottom: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    min-width: 0;

    &:last-child {
        border-bottom: 0;
    }

    @media (max-width: 760px) {
        grid-template-columns: 56px minmax(0, 1fr);
    }
`;

const MarketplaceOrderOptionalColumn = styled.div`
    @media (max-width: 760px) {
        display: none;
    }
`;

export const MarketplaceOrderPageSkeleton: FC = () => (
    <MarketplaceOrderSkeletonShell aria-hidden='true'>
        <MarketplaceOrderHeaderSkeleton>
            <SkeletonBlock $width='90px' $height='14px' />
            <SkeletonBlock $width='18px' $height='14px' />
            <SkeletonBlock $width='120px' $height='14px' />
        </MarketplaceOrderHeaderSkeleton>
        <MarketplaceOrderPanelSkeleton>
            <MarketplaceOrderToolbarSkeleton>
                <SkeletonRow $gap='12px'>
                    <SkeletonBlock $width='180px' $height='24px' />
                    <SkeletonBlock $width='86px' $height='22px' $radius='11px' />
                    <SkeletonBlock $width='170px' $height='13px' />
                </SkeletonRow>
                <SkeletonRow $gap='8px'>
                    <SkeletonBlock $width='128px' $height='36px' $radius='6px' />
                    <SkeletonBlock $width='128px' $height='36px' $radius='6px' />
                </SkeletonRow>
            </MarketplaceOrderToolbarSkeleton>
            <MarketplaceOrderTableSkeleton>
                {Array.from({ length: 5 }).map((_, index) => (
                    <MarketplaceOrderTableRowSkeleton key={index}>
                        <SkeletonBlock $width='56px' $height='56px' $radius='6px' />
                        <SkeletonStack $gap='8px'>
                            <SkeletonBlock $width={index % 2 === 0 ? '72%' : '56%'} $height='15px' />
                            <SkeletonBlock $width='42%' $height='12px' />
                        </SkeletonStack>
                        <MarketplaceOrderOptionalColumn>
                            <SkeletonBlock $width='94px' $height='13px' />
                        </MarketplaceOrderOptionalColumn>
                        <MarketplaceOrderOptionalColumn>
                            <SkeletonBlock $width='70px' $height='22px' $radius='11px' />
                        </MarketplaceOrderOptionalColumn>
                    </MarketplaceOrderTableRowSkeleton>
                ))}
            </MarketplaceOrderTableSkeleton>
        </MarketplaceOrderPanelSkeleton>
    </MarketplaceOrderSkeletonShell>
);
